/**
 * WhiteLabelManager.tsx
 * Admin UI for configuring per-org white-label theme:
 * logo upload, favicon upload, colour palette, email settings, domain config.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Palette, Globe, Mail, Upload, RefreshCw, Save, Trash2, Eye } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { tv, rich } from './i18nUtil';
import { useAuthStore } from '../../stores';

/** Only platform admins may set domains and custom CSS (admin-service enforces this). */
const PLATFORM_ROLES = new Set(['super_admin', 'super-admin', 'admin']);

// ── Types ─────────────────────────────────────────────────────────────────────

interface OrgTheme {
  display_name?: string;
  logo_url?: string;
  favicon_url?: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  background_color: string;
  surface_color: string;
  text_color: string;
  text_muted_color: string;
  font_family: string;
  font_size_base: number;
  email_from_name?: string;
  email_from_address?: string;
  email_footer_html?: string;
  custom_domain?: string;
  subdomain?: string;
  css_overrides?: string;
  terms_url?: string;
  privacy_url?: string;
  support_url?: string;
  hide_eurion_branding: boolean;
  allow_guest_access: boolean;
}

const DEFAULTS: OrgTheme = {
  primary_color: '#3B82F6',
  secondary_color: '#1E3A5F',
  accent_color: '#10B981',
  background_color: '#F8FAFC',
  surface_color: '#FFFFFF',
  text_color: '#111827',
  text_muted_color: '#6B7280',
  font_family: 'Inter, system-ui, sans-serif',
  font_size_base: 14,
  hide_eurion_branding: false,
  allow_guest_access: true,
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function ColorField({ label, field, value, onChange }: {
  label: string; field: string; value: string;
  onChange: (field: string, val: string) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={value}
        onChange={e => onChange(field, e.target.value)}
        className="w-10 h-10 rounded cursor-pointer border border-slate-200 p-0.5"
      />
      <div className="flex-1">
        <label className="block text-xs font-medium text-slate-500 mb-0.5">{label}</label>
        <input
          type="text"
          value={value}
          onChange={e => onChange(field, e.target.value)}
          pattern="^#[0-9A-Fa-f]{6}$"
          className="w-full rounded border border-slate-200 px-2 py-1 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>
  );
}

function ImageUploader({ label, uploadLabel, currentUrl, accept, maxKb, onUploaded }: {
  label: string; uploadLabel: string; currentUrl?: string; accept: string; maxKb: number;
  onUploaded: (url: string) => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxKb * 1024) {
      setError(tv(t, 'adminUi.whiteLabel.fileTooLarge', 'File must be ≤ {{n}}KB', { n: maxKb }));
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      // URL determined by parent — we just upload and get back the URL
      const res = await fetch((e.target as HTMLInputElement).dataset.uploadUrl!, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('eurion_token')}` },
        body: form,
      });
      if (!res.ok) throw new Error(tv(t, 'adminUi.whiteLabel.uploadFailedStatus', 'Upload failed: {{status}}', { status: res.status }));
      const data = await res.json();
      onUploaded(data.logo_url ?? data.favicon_url ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('adminUi.whiteLabel.uploadFailed', 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      {currentUrl && (
        <img src={currentUrl} alt={label} loading="lazy" className="h-12 object-contain rounded border border-slate-200 p-1 bg-white" />
      )}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
      >
        {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {uploading ? t('adminUi.whiteLabel.uploading', 'Uploading…') : uploadLabel}
      </button>
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={handleFile} aria-label={uploadLabel} />
      {error && <p className="text-red-500 text-xs">{error}</p>}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

interface Props {
  orgId: string;
}

type Tab = 'branding' | 'colours' | 'email' | 'domain' | 'advanced';

export function WhiteLabelManager({ orgId }: Props) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('branding');
  const isPlatformAdmin = useAuthStore(s => s.user?.roles?.some(r => PLATFORM_ROLES.has(r)) ?? false);
  const [theme, setTheme] = useState<OrgTheme>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);

  // Load current theme
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/api/v1/orgs/${orgId}/theme`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('eurion_token')}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (!data._defaults) setTheme({ ...DEFAULTS, ...data });
        }
      } catch { /* use defaults */ }
      finally { setLoading(false); }
    })();
  }, [orgId]);

  const update = useCallback(<K extends keyof OrgTheme>(field: K, value: OrgTheme[K]) => {
    setTheme(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleColorChange = useCallback((field: string, val: string) => {
    update(field as keyof OrgTheme, val as any);
  }, [update]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/orgs/${orgId}/theme`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('eurion_token')}`,
        },
        body: JSON.stringify(theme),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('adminUi.whiteLabel.saveFailed', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!confirm(t('adminUi.whiteLabel.confirmReset', 'Reset to platform defaults? This cannot be undone.'))) return;
    await fetch(`/api/v1/orgs/${orgId}/theme`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('eurion_token')}` },
    });
    setTheme(DEFAULTS);
  };

  if (loading) {
    return <div className="flex items-center gap-2 p-6 text-slate-500"><RefreshCw className="w-4 h-4 animate-spin" /> {t('adminUi.whiteLabel.loading', 'Loading theme…')}</div>;
  }

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'branding', label: t('adminUi.whiteLabel.tabBranding', 'Branding'), icon: <Upload className="w-4 h-4" /> },
    { id: 'colours',  label: t('adminUi.whiteLabel.tabColours', 'Colours'),  icon: <Palette className="w-4 h-4" /> },
    { id: 'email',    label: t('adminUi.whiteLabel.tabEmail', 'Email'),    icon: <Mail className="w-4 h-4" /> },
    { id: 'domain',   label: t('adminUi.whiteLabel.tabDomain', 'Domain'),   icon: <Globe className="w-4 h-4" /> },
    { id: 'advanced', label: t('adminUi.whiteLabel.tabAdvanced', 'Advanced'), icon: <Eye className="w-4 h-4" /> },
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">{t('adminUi.whiteLabel.title', 'White-label Theme')}</h2>
          <p className="text-sm text-slate-500 mt-0.5">{t('adminUi.whiteLabel.subtitle', "Customise your organisation's branding, colours, and domain.")}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Live preview toggle */}
          <button
            type="button"
            onClick={() => setPreview(p => !p)}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border',
              preview ? 'bg-blue-50 text-blue-700 border-blue-200' : 'text-slate-600 border-slate-200 hover:bg-slate-50',
            )}
          >
            <Eye className="w-4 h-4" />
            {preview ? t('adminUi.whiteLabel.previewing', 'Previewing') : t('adminUi.whiteLabel.preview', 'Preview')}
          </button>
          <button
            type="button"
            onClick={resetToDefaults}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-slate-200 text-slate-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
          >
            <Trash2 className="w-4 h-4" />
            {t('adminUi.whiteLabel.reset', 'Reset')}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saved ? t('adminUi.whiteLabel.saved', 'Saved!') : saving ? t('adminUi.saving', 'Saving…') : t('adminUi.whiteLabel.save', 'Save Changes')}
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-slate-100">
        {tabs.map(tb => (
          <button
            key={tb.id}
            type="button"
            onClick={() => setTab(tb.id)}
            className={clsx(
              'flex items-center gap-1.5 px-5 py-3 text-sm font-medium border-b-2 transition-colors',
              tab === tb.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-200',
            )}
          >
            {tb.icon}{tb.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-6 space-y-6">

        {/* ── Branding ── */}
        {tab === 'branding' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{t('adminUi.whiteLabel.displayName', 'Display Name')}</label>
              <input
                type="text"
                value={theme.display_name ?? ''}
                onChange={e => update('display_name', e.target.value)}
                placeholder="Acme Corp"
                className="w-full max-w-sm rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-slate-400 mt-1">{t('adminUi.whiteLabel.displayNameHint', 'Shown in the browser tab and email subjects.')}</p>
            </div>
            <ImageUploader
              label={t('adminUi.whiteLabel.logo', 'Logo')}
              uploadLabel={t('adminUi.whiteLabel.uploadLogo', 'Upload Logo')}
              currentUrl={theme.logo_url}
              accept="image/svg+xml,image/png,image/webp"
              maxKb={2048}
              onUploaded={url => update('logo_url', url)}
            />
            <ImageUploader
              label={t('adminUi.whiteLabel.favicon', 'Favicon')}
              uploadLabel={t('adminUi.whiteLabel.uploadFavicon', 'Upload Favicon')}
              currentUrl={theme.favicon_url}
              accept="image/x-icon,image/png,image/svg+xml"
              maxKb={256}
              onUploaded={url => update('favicon_url', url)}
            />
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="hide-branding"
                checked={theme.hide_eurion_branding}
                onChange={e => update('hide_eurion_branding', e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-blue-600"
              />
              <label htmlFor="hide-branding" className="text-sm text-slate-700">
                {t('adminUi.whiteLabel.hideBranding', 'Hide "Powered by Eurion" footer')}
              </label>
            </div>
          </div>
        )}

        {/* ── Colours ── */}
        {tab === 'colours' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              { label: t('adminUi.whiteLabel.colorPrimary', 'Primary'), field: 'primary_color' },
              { label: t('adminUi.whiteLabel.colorSecondary', 'Secondary'), field: 'secondary_color' },
              { label: t('adminUi.whiteLabel.colorAccent', 'Accent'), field: 'accent_color' },
              { label: t('adminUi.whiteLabel.colorBackground', 'Background'), field: 'background_color' },
              { label: t('adminUi.whiteLabel.colorSurface', 'Surface'), field: 'surface_color' },
              { label: t('adminUi.whiteLabel.colorText', 'Text'), field: 'text_color' },
              { label: t('adminUi.whiteLabel.colorTextMuted', 'Text Muted'), field: 'text_muted_color' },
            ].map(c => (
              <ColorField
                key={c.field}
                label={c.label}
                field={c.field}
                value={(theme as Record<string, any>)[c.field] ?? '#000000'}
                onChange={handleColorChange}
              />
            ))}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">{t('adminUi.whiteLabel.fontFamily', 'Font Family')}</label>
              <input
                type="text"
                value={theme.font_family}
                onChange={e => update('font_family', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {/* ── Email ── */}
        {tab === 'email' && (
          <div className="space-y-4 max-w-lg">
            {[
              { label: t('adminUi.whiteLabel.fromName', 'From Name'), field: 'email_from_name', placeholder: 'Acme Corp via Eurion' },
              { label: t('adminUi.whiteLabel.fromAddress', 'From Address'), field: 'email_from_address', placeholder: 'noreply@acme.eu', type: 'email' },
            ].map(f => (
              <div key={f.field}>
                <label className="block text-sm font-medium text-slate-700 mb-1">{f.label}</label>
                <input
                  type={f.type ?? 'text'}
                  value={(theme as Record<string, any>)[f.field] ?? ''}
                  onChange={e => update(f.field as keyof OrgTheme, e.target.value as any)}
                  placeholder={f.placeholder}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ))}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{t('adminUi.whiteLabel.emailFooter', 'Email Footer HTML')}</label>
              <textarea
                value={theme.email_footer_html ?? ''}
                onChange={e => update('email_footer_html', e.target.value)}
                rows={4}
                placeholder="<p>© 2025 Acme Corp — Powered by Eurion</p>"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-slate-400 mt-1">{t('adminUi.whiteLabel.emailFooterHint', 'Basic HTML only (b, i, a, p, span). Sanitised server-side.')}</p>
            </div>
          </div>
        )}

        {/* ── Domain ── */}
        {tab === 'domain' && (
          <div className="space-y-4 max-w-lg">
            {isPlatformAdmin && (<>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{t('adminUi.whiteLabel.customDomain', 'Custom Domain')}</label>
              <input
                type="text"
                value={theme.custom_domain ?? ''}
                onChange={e => update('custom_domain', e.target.value)}
                placeholder="meet.acme.eu"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-slate-400 mt-1">{rich(t('adminUi.whiteLabel.customDomainHint', 'Point a CNAME record to <code>gateway.eurion.eu</code>. TLS provisioned automatically.'), { code: 'bg-slate-100 px-1 rounded' })}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{t('adminUi.whiteLabel.subdomain', 'Subdomain')}</label>
              <div className="flex rounded-lg overflow-hidden border border-slate-200 focus-within:ring-2 focus-within:ring-blue-500">
                <input
                  type="text"
                  value={theme.subdomain ?? ''}
                  onChange={e => update('subdomain', e.target.value)}
                  placeholder="acme"
                  className="flex-1 px-3 py-2 text-sm focus:outline-none"
                />
                <span className="flex items-center px-3 bg-slate-50 border-l border-slate-200 text-slate-500 text-sm">.eurion.eu</span>
              </div>
            </div>
            </>)}
            {[
              { label: t('adminUi.whiteLabel.termsUrl', 'Terms of Service URL'), field: 'terms_url' },
              { label: t('adminUi.whiteLabel.privacyUrl', 'Privacy Policy URL'), field: 'privacy_url' },
              { label: t('adminUi.whiteLabel.supportUrl', 'Support URL'), field: 'support_url' },
            ].map(f => (
              <div key={f.field}>
                <label className="block text-sm font-medium text-slate-700 mb-1">{f.label}</label>
                <input
                  type="url"
                  value={(theme as Record<string, any>)[f.field] ?? ''}
                  onChange={e => update(f.field as keyof OrgTheme, e.target.value as any)}
                  placeholder="https://acme.eu/terms"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ))}
          </div>
        )}

        {/* ── Advanced ── */}
        {tab === 'advanced' && (
          <div className="space-y-4">
            {isPlatformAdmin && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{t('adminUi.whiteLabel.customCss', 'Custom CSS')}</label>
              <textarea
                value={theme.css_overrides ?? ''}
                onChange={e => update('css_overrides', e.target.value)}
                rows={10}
                spellCheck={false}
                placeholder={`.eurion-sidebar { border-radius: 0; }\n.eurion-btn-primary { letter-spacing: 0.05em; }`}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-slate-400 mt-1">
                {rich(t('adminUi.whiteLabel.customCssHint', 'Injected into the app after all default styles. External <code>@import</code> and <code>javascript:</code> are stripped server-side.'), { code: 'bg-slate-100 px-0.5 rounded' })}
              </p>
            </div>
            )}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="guest-access"
                checked={theme.allow_guest_access}
                onChange={e => update('allow_guest_access', e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-blue-600"
              />
              <label htmlFor="guest-access" className="text-sm text-slate-700">
                {t('adminUi.whiteLabel.allowGuests', 'Allow guest access (join meetings without login)')}
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Live preview pane */}
      {preview && (
        <div
          className="mx-6 mb-6 rounded-xl border border-slate-200 overflow-hidden"
          style={{ background: theme.background_color }}
        >
          <div
            className="px-5 py-3 flex items-center gap-3"
            style={{ background: theme.secondary_color }}
          >
            {theme.logo_url ? (
              <img src={theme.logo_url} alt={t('adminUi.whiteLabel.logo', 'Logo')} loading="lazy" className="h-7 object-contain" />
            ) : (
              <span className="font-bold text-white text-lg">{theme.display_name ?? 'EURION'}</span>
            )}
          </div>
          <div className="p-5 space-y-3">
            <p className="text-sm font-medium" style={{ color: theme.text_color }}>
              {t('adminUi.whiteLabel.sampleMessage', 'Sample message from a colleague')}
            </p>
            <p className="text-xs" style={{ color: theme.text_muted_color }}>
              {t('adminUi.whiteLabel.sampleMeta', 'Just now · Eurion Chat')}
            </p>
            <button
              className="px-4 py-2 rounded-lg text-white text-sm font-medium"
              style={{ background: theme.primary_color }}
            >
              {t('adminUi.whiteLabel.joinMeeting', 'Join Meeting')}
            </button>
            <div className="inline-block ml-2 px-3 py-1.5 rounded-full text-white text-xs" style={{ background: theme.accent_color }}>
              {tv(t, 'adminUi.whiteLabel.unread', '{{n}} unread', { n: 3 })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
